// frontend/src/components/cohesion/EntityDrawer.Item.tsx
// Inventory item panel for the universal entity drawer.

import { useEffect, useState } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'

type Lang = { t: (k: string) => string }

interface Props {
  id: number
  navigate: (to: string) => void
  closeDrawer: () => void
}

interface Item {
  id: number
  name: string
  asset_id?: string
  status?: string
  location?: string
}

export default function EntityDrawerItem({ id, navigate, closeDrawer }: Props) {
  const { t } = useLanguage() as Lang
  const [data, setData] = useState<Item | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<Item>(`/items/${id}`)
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(String(e?.response?.data?.error ?? e?.message ?? 'load failed')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) return <div className="entity-drawer-loading">{t('loading')}</div>
  if (error)   return <div className="entity-drawer-error" role="alert">{error}</div>
  if (!data)   return <div className="entity-drawer-empty">{t('itemNotFound')}</div>

  const openItem = () => {
    closeDrawer()
    navigate(`/items/${data.id}`)
  }

  return (
    <>
      <button type="button" className="entity-drawer-title-link" onClick={openItem}>
        {data.name}
      </button>
      <div className="entity-drawer-meta">
        {data.asset_id && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('assetId')}</span>
            <span className="entity-drawer-meta-val">{data.asset_id}</span>
          </div>
        )}
        {data.status && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('status')}</span>
            <span className="entity-drawer-meta-val">{data.status}</span>
          </div>
        )}
        {data.location && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('location')}</span>
            <span className="entity-drawer-meta-val">{data.location}</span>
          </div>
        )}
      </div>
      <div className="entity-drawer-actions">
        <button type="button" className="entity-drawer-action" onClick={openItem}>
          {t('openWorkspace')}
        </button>
      </div>
    </>
  )
}
